<template lang="pug">
div(class="notifications-center")
  .notification-bell(@click="togglePanel")
    i.fa-solid.fa-bell(:class="{'fa-shake': hasUnread}")
    span.badge.badge-danger.notification-count(v-if="unreadCount > 0") {{unreadCount}}

  transition(name="slide-fade")
    .notification-panel(v-if="showPanel" v-click-outside="closePanel")
      .panel-header
        h5 {{$I18n.t('vue.notifications.title')}}
        .header-actions
          button.btn.btn-sm.btn-link(@click="markAllAsRead" v-if="unreadCount > 0")
            i.fa-solid.fa-check-double
            span.ms-1 {{$I18n.t('vue.notifications.mark_all_read')}}
          button.btn.btn-sm.btn-link(@click="closePanel")
            i.fa-solid.fa-times

      .panel-body
        .loading-spinner(v-if="loading")
          .spinner-border.spinner-border-sm
        
        .notification-list(v-else-if="notifications.length > 0")
          .notification-item(
            v-for="notification in notifications"
            :key="notification.id"
            :class="{'unread': !notification.read, ['priority-' + notification.priority]: true}"
            @click="handleNotificationClick(notification)"
          )
            .notification-icon
              i.fa-solid(:class="getIconClass(notification.type)")
            .notification-content
              .notification-title {{notification.title}}
              .notification-message {{notification.message}}
              .notification-time {{formatTime(notification.created_at)}}
            .notification-actions
              button.btn.btn-sm.btn-link(@click.stop="markAsRead(notification)" v-if="!notification.read")
                i.fa-solid.fa-check
              button.btn.btn-sm.btn-link.text-danger(@click.stop="deleteNotification(notification)")
                i.fa-solid.fa-trash

        .empty-state(v-else)
          i.fa-solid.fa-inbox.fa-3x.text-muted
          p.mt-3.text-muted {{$I18n.t('vue.notifications.no_notifications')}}

      .panel-footer
        a(:href="'/notifications'" class="btn btn-sm btn-primary btn-block")
          | {{$I18n.t('vue.notifications.view_all')}}
</template>

<script>
const { BackendService } = require('app/services');

export default {
  data() {
    return {
      showPanel: false,
      notifications: [],
      unreadCount: 0,
      loading: false,
      pollingInterval: null
    };
  },
  computed: {
    hasUnread() {
      return this.unreadCount > 0;
    }
  },
  mounted() {
    this.loadNotifications();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    togglePanel() {
      this.showPanel = !this.showPanel;
      if (this.showPanel) {
        this.loadNotifications();
      }
    },
    closePanel() {
      this.showPanel = false;
    },
    loadNotifications() {
      this.loading = true;
      BackendService.get('/notifications.json')
        .then(response => {
          // BackendService.get() ya parsea JSON, response ES el objeto data
          this.notifications = response.notifications || [];
          this.unreadCount = response.unread_count || 0;
          this.loading = false;
        })
        .catch(error => {
          console.error('Error loading notifications:', error);
          this.loading = false;
        });
    },
    updateUnreadCount() {
      BackendService.get('/notifications/unread_count.json')
        .then(response => {
          // BackendService.get() ya parsea JSON, response ES el objeto data
          this.unreadCount = response.count || 0;
        })
        .catch(error => {
          console.error('Error updating unread count:', error);
        });
    },
    markAsRead(notification) {
      BackendService.post(`/notifications/${notification.id}/mark_as_read`)
        .then(() => {
          notification.read = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        })
        .catch(error => {
          console.error('Error marking as read:', error);
        });
    },
    markAllAsRead() {
      BackendService.post('/notifications/mark_all_as_read')
        .then(() => {
          this.notifications.forEach(n => n.read = true);
          this.unreadCount = 0;
        })
        .catch(error => {
          console.error('Error marking all as read:', error);
        });
    },
    deleteNotification(notification) {
      if (confirm(this.$I18n.t('vue.notifications.confirm_delete'))) {
        BackendService.delete(`/notifications/${notification.id}`)
          .then(() => {
            const index = this.notifications.indexOf(notification);
            if (index > -1) {
              this.notifications.splice(index, 1);
              if (!notification.read) {
                this.unreadCount = Math.max(0, this.unreadCount - 1);
              }
            }
          })
          .catch(error => {
            console.error('Error deleting notification:', error);
          });
      }
    },
    handleNotificationClick(notification) {
      if (!notification.read) {
        this.markAsRead(notification);
      }
      if (notification.related_url) {
        window.location.href = notification.related_url;
      }
    },
    getIconClass(type) {
      const icons = {
        'order_assigned': 'fa-clipboard-check',
        'order_completed': 'fa-check-circle',
        'invoice_paid': 'fa-dollar-sign',
        'invoice_overdue': 'fa-exclamation-triangle',
        'maintenance_due': 'fa-tools',
        'maintenance_reminder': 'fa-calendar-check',
        'system': 'fa-info-circle'
      };
      return icons[type] || 'fa-bell';
    },
    formatTime(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return this.$I18n.t('vue.notifications.time.just_now');
      if (diffMins < 60) return this.$I18n.t('vue.notifications.time.minutes_ago', {count: diffMins});
      if (diffHours < 24) return this.$I18n.t('vue.notifications.time.hours_ago', {count: diffHours});
      if (diffDays < 7) return this.$I18n.t('vue.notifications.time.days_ago', {count: diffDays});
      return date.toLocaleDateString();
    },
    startPolling() {
      // Poll every 30 seconds for new notifications
      this.pollingInterval = setInterval(() => {
        this.updateUnreadCount();
      }, 30000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  }
};
</script>

<style scoped>
.notifications-center {
  position: relative;
}

.notification-bell {
  cursor: pointer;
  position: relative;
  padding: 10px;
  font-size: 1.2rem;
}

.notification-bell:hover {
  opacity: 0.8;
}

.notification-count {
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 0.7rem;
  min-width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-panel {
  position: absolute;
  top: 100%;
  right: 0;
  width: 400px;
  max-width: 90vw;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1050;
  margin-top: 10px;
}

.panel-header {
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-header h5 {
  margin: 0;
  font-size: 1.1rem;
}

.header-actions {
  display: flex;
  gap: 5px;
}

.panel-body {
  max-height: 400px;
  overflow-y: auto;
}

.notification-list {
  padding: 0;
}

.notification-item {
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  gap: 10px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.notification-item:hover {
  background-color: #f8f9fa;
}

.notification-item.unread {
  background-color: #e7f3ff;
}

.notification-item.priority-urgent {
  border-left: 3px solid #dc3545;
}

.notification-item.priority-high {
  border-left: 3px solid #ffc107;
}

.notification-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #495057;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.notification-message {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-time {
  font-size: 0.75rem;
  color: #999;
}

.notification-actions {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
}

.panel-footer {
  padding: 10px 15px;
  border-top: 1px solid #eee;
}

.loading-spinner {
  padding: 40px;
  text-align: center;
}

.slide-fade-enter-active {
  transition: all 0.2s ease;
}

.slide-fade-leave-active {
  transition: all 0.15s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
